import {
  BTypeFacilityMainHead,
  CTypeMap,
  DTypeTabsFacility,
  DefaultFacility,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  buildFacilityPageSchema,
  GOI_WEB_URL,
  RichSearch,
  ParlorHeadSection,
  ATypeFacilityFAQ,
  FacilityDetailFAQ,
  CTypeFacilityList,
  NewCTypeCTA,
  excludeFacilities,
  FacilityHiddenNotice,
  getFacilityTypeLabel,
  getFacilityDisplayName,
  getFacilityRegionKeyword,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";

import facilities from "goi_common/src/constants/facilities_detail.json";

import { parlorRegion } from "@/constants/parlorRegion";
import { Stack } from "@mui/material";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = facilities
    .filter((facility) => targetSidos.includes(facility.sido))
    .map((facility) => ({
      params: {
        id: String(facility.id),
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

/** TODO: title, metatag 작업해야함 */
export const getStaticProps: GetStaticProps = async ({ params }) => {
  const id = params?.id as string;

  const p_facility = facilities.find(
    (facility) => Number(facility.id) === Number(id)
  ) as DefaultFacility;

  const { address, companyname, images, type } = p_facility;
  const typeLabel = getFacilityTypeLabel(type);
  const displayName = getFacilityDisplayName(companyname, type);
  const regionKeyword = getFacilityRegionKeyword(type);

  const 시가아닌_도인경우 = address.split(" ")[0].length == 4;
  const district = 시가아닌_도인경우
    ? address[0] + address[2]
    : address.slice(0, 2);

  const title = `[${district}] ${displayName}의 총 정보 안내 및 요약 :: 부산울산경남장례정보포털`;
  const description = `${companyname} 위치 안내. 주소: ${address}. ${companyname} 관련된 정보, 상세 비용 정보, 예약 설명, 장례 관련 서비스 및 방법`;
  const keywords = `${companyname}, ${companyname} 비용, ${companyname}평균 예상 비용, ${companyname} 자연장, ${companyname} 수목장, ${companyname} 추천,
  ${companyname} 시설, ${companyname} 위치, ${companyname} 주소, ${displayName}, ${companyname} 후기`;

  return {
    props: {
      defaultFacility: p_facility,
      pageDescription: description,
      pageKeywords: keywords,
      pageOgTitle: title,
      pageOgImage: images[0].url || "",
    },
  };
};

interface FacilityDetailPagProps {
  pageOgTitle: string;
  defaultFacility: DefaultFacility;
}

const FacilityDetailPage = ({
  pageOgTitle,
  defaultFacility,
}: FacilityDetailPagProps) => {
  const sidoName = FUNERAL_SIDO.get(Number(defaultFacility.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultFacility.sido))?.get(
    Number(defaultFacility.sigungu)
  );

  const facilityPageSchema = buildFacilityPageSchema(defaultFacility);

  const isHidden = excludeFacilities.includes(String(defaultFacility.id));

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>

        {/* <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqList) }}
        /> */}
      </Head>
      <RichSearch data={facilityPageSchema} />

      <ParlorHeadSection
        component={
          <>
            <BTypeFacilityMainHead
              sidoName={sidoName}
              sigunguName={sigunguName}
              defaultFacility={defaultFacility}
              title={`${defaultFacility.companyname}`}
              type="jangji"
            />
          </>
        }
      />
      {isHidden ? (
        <FacilityHiddenNotice
          companyname={defaultFacility.companyname}
          mainColor={regionColor}
          ctaHref={`${GOI_WEB_URL}/facilities/search/initial/?referrerId=${defaultFacility.id}&utm_source=dogsound`}
          event={{
            domain: "gyeongsang",
            path: "facility",
            trigger_and_target: "CTA_uv_facil_req",
          }}
        />
      ) : (
        <>
      <Stack
        style={{
          background: "white",
          margin: "20px 12px",
          padding: "40px 0px 20px",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <NewCTypeCTA
          mainColor={regionColor}
          primary="장지 비용 알아보기"
          secondary={`고이에서는\n e하늘 장사정보 시스템을 기반으로\n ${defaultFacility.companyname}의\n 상세 비용을 안내해드립니다.`}
          href={`${GOI_WEB_URL}/match/facility-requests/${defaultFacility.id}/?utm_source=dogsound`}
          event={{
            domain: "gyeongsang",
            path: "facility",
            trigger_and_target: "CTA_uv_facil_req",
          }}
        />
      </Stack>
      <Stack style={{ background: "white" }}>
        <DTypeTabsFacility
          mainColor={regionColor}
          href={`facility-company/${defaultFacility.id}/`}
          selectTab="소개"
          event={{
            domain: "gyeongsang",
            path: "facility",
            trigger_and_target: "DType_facil_tab",
          }}
        />
      </Stack>

      <Stack
        style={{
          background: "white",
          margin: "20px 12px",
          padding: "40px 20px 20px",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <CTypeMap defaultParlor={defaultFacility} />
      </Stack>

      <Stack
        style={{
          background: "white",
          margin: "20px 12px",
          padding: "40px 20px 20px",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <ATypeFacilityFAQ
          defaultFacility={defaultFacility}
          event={{
            domain: "gyeongsang",
            path: "facility",
          }}
        />
        <FacilityDetailFAQ />
      </Stack>
        </>
      )}

      <CTypeFacilityList
        sido={defaultFacility.sido}
        title={`근처 장지 (${sidoName} 전체)`}
        event={{
          domain: "gyeongsang",
          path: "facility",
          trigger_and_target: "uv_facility",
        }}
        defaultHref={"/facility-company"}
        mainColor={regionColor}
      />
    </>
  );
};

export default FacilityDetailPage;
